import axios from 'axios';
import Cookies from 'js-cookie';
import Qs from 'qs';
import SessionStorageService from './SessionStorageService';
import UserService from './UserService';
import getSlsBasePath from '../utils/getSlsBasePath/getSlsBasePath';
import { hasLength } from '../utils';
import * as R from 'ramda';
import { slsStage, tasVersion } from 'App/components/config/config';
import getData from 'App/utils/getData';
import TokenManagerService from 'App/services/TokenManagerService';
import createAxios from 'App/utils/createAxios';

const slsEnv = { slsStage };

const slsBasePath = getSlsBasePath(slsEnv, 'user');

class AuthService {
    /**
     * Stores the endpoint url for TAS
     */
    static endpoint = `/tas/${tasVersion}`;

    /**
     * @ngdoc method
     * @name authService#changeBranch
     *
     * @description
     * Changes the branch a users session is attached to.
     *
     * @param {string} divisionId - The id of the division the user will be switched to.
     */
    static changeBranch = async (divisionId) => {
        await UserService.setBranch(divisionId);
        await this.updateJwt();
    };

    /**
     * We no longer use passwords, but instead use sso
     * @deprecated
     * @param currentPassword
     * @param newPassword
     * @returns {Promise<any|string>}
     */
    static changePassword = async (currentPassword, newPassword) => {
        const session = TokenManagerService.getAccessToken();
        const url = `${this.endpoint}/user/changePassword`;
        const data = { currentPassword, newPassword };
        const headers = {
            Authorization: `Bearer ${session}`,
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        };

        const response = await axios.patch(url, data, {
            paramsSerializer(params) {
                // Equivalent to angular's $httpParamSerializerJQLike
                return Qs.stringify(params, { arrayFormat: 'brackets' });
            },
            withCredentials: true,
            headers,
        });
        return response.data;
    };

    /**
     * @ngdoc method
     * @name authService#get
     *
     * @description
     * Gets the current session token from one of two places:
     * 1. The currentToken object in local storage
     * 2. The rootscope of the application
     * 2. The Cookie for SSO users
     *
     * If no key is found, it will redirect to the login page.
     *
     * @returns {string|null} The session token if it exists, or null
     */
    static get = () => {
        const token = TokenManagerService.getAccessToken();
        if (Cookies.get('sso_jwt') && !token) {
            // If the user is arriving from single sign-on we need to set these values.
            SessionStorageService.getJwt();
            return this.get();
        }
        if (typeof token === 'string') {
            return token;
        }
        return null;
    };

    static getTargetPage = () => {
        const target = window.localStorage.getItem('targetPage');
        if (typeof target === 'string') {
            /**
             * Ensure we're not returning recursive URLs like
             * "http://stm.local/#!/http://stm.local
             */
            return target.replace(/https?:\/\/[^/]+(?:\/#!)?/, '');
        }
        return null;
    };

    /**
     * Clears anything stored for target page.
     * @returns {boolean}
     */
    static clearTargetPage = () => {
        window.localStorage.removeItem('targetPage');
        return true;
    };

    /**
     * @ngdoc method
     * @name authService#getUser
     *
     * @description
     * Gets User's Object
     */
    static getUser = () => {
        return UserService.get();
    };

    /**
     * Fetches branches the user has access to.
     * @param {Boolean} setInLocalStorage - If true, will set the userBranches in localStorage
     * @returns {<Promise>.UserBranches} - Resolves into the User Branches object keyed by Id
     */
    static fetchUserBranches = async (setInLocalStorage = false) => {
        const url = `${slsBasePath}/1.0.0/branches`;

        const data = await createAxios({}).get(url);
        if (setInLocalStorage) {
            // Clean createdBy, and modifiedBy keys containing user ids
            const userBranches = R.compose(
                R.tryCatch(JSON.stringify, R.always('{}')),
                R.map(R.pick(['id', 'name', 'createdOn', 'modifiedOn']))
            )(data);

            window.localStorage.setItem('userBranches', userBranches);
        }
        return data;
    };

    /**
     * Gets and parses userBranches object string from localStorage.
     * @param {Boolean} returnArray - If true, will return an array of branches objects.
     * @returns {null|Array.<Object>|Object}
     */
    static getUserBranches = (returnArray = false) => {
        const userBranches = window.localStorage.getItem('userBranches');
        if (userBranches) {
            const parsedUserBranches = JSON.parse(userBranches);
            if (hasLength(Object.keys(parsedUserBranches))) {
                if (returnArray) {
                    return Object.values(parsedUserBranches);
                }
                return parsedUserBranches;
            }
        }
        return null;
    };

    static setTargetPage = (target) => {
        window.localStorage.setItem('targetPage', target);
    };

    /**
     * @ngdoc method
     * @name authService#remove
     *
     * @description
     * Removes the current token
     */
    static clearSession = () => {
        TokenManagerService.markLoggedOut();
        TokenManagerService.clearTokens();
        window.localStorage.clear();
    };

    /**
     * @ngdoc method
     * @name authService#logout
     *
     * @description
     * Logs out the current user and removes their token.
     *
     */
    static logout = async () => {
        const url = '/stm/logout.php?angular=true';
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        };
        try {
            const { default: Axios } = await import('axios');

            // Retry revoke up to 3 times
            let revoked = false;

            for (let attempt = 0; attempt < 3 && !revoked; attempt += 1) {
                try {
                    await Axios.post('/tas/1.0.0/revoke', null, {
                        headers: { 'X-App-Id': 'stm' },
                    });
                    revoked = true;
                } catch (error) {
                    // don't block logout if revoke fails.
                    // eslint-disable-next-line no-console
                    console.error(`Revoke attempt ${attempt + 1} failed`, error);
                }
            }
            await Axios.post(url, {}, { headers });
            this.clearSession();
        } catch (_err) {
            this.clearSession();
        }
    };

    /**
     * @ngdoc method
     * @name authService#set
     *
     * @description
     * Sets a user token into local storage. This method is used to set the 'currentToken' value in local storage, this
     * action needs to occur here as requring SessionStorage will create a circular dependency. *
     * @param {string} token - The token value
     */
    static set = (token) => {
        TokenManagerService.setTokens({ access: token });
    };

    static getSsoUrl({ email }) {
        /**
         * This is an unauthenticated route. Do not provide token.
         * PLAT-119: sso_redirect was migrated from the serverless TAS (`/sls-tas/1.0.0`)
         * to the long-lived EKS TAS service (`/tas/1.0.0`). Point at the EKS TAS base.
         */
        const url = `/tas/${tasVersion}`;
        return axios.post(`${url}/sso_redirect`, { email }).then(getData).then(R.prop('ssoLoginUrl'));
    }

    /**
     * @ngdoc method
     * @name authService#updateJwt
     *
     * @description
     * Fetches an update JWT from Therigy Authentication Service
     *
     * @returns {Promise}
     */
    static updateJwt = async () => {
        // In order to ensure that tokens remain fresh, we request a new one every time there is UI activity.
        const session = await TokenManagerService.refreshAccessToken();
        if (typeof session !== 'string') {
            return false;
        }
        return session;
    };
}

export default AuthService;
